<template>
  <div class="check-list shop-page__filter-section" :class="rootClasses">
    <div class="shop-page__filter-section-header" @click="selectionOpen = !selectionOpen">
      <div class="shop-page__filter-section-icon">
        <slot />
      </div>
      <div class="shop-page__filter-section-title">
        <span>
          {{ listName }}
        </span>
        <div v-if="flatTree" class="check-list__meta">
          <span class="shop-page__filter-section-counter" v-if="selectedItems.length > 0">
            {{ selectedItems.length }}
          </span>
          <button class="check-list__meta-toggle" @click.stop="selectionOpen = !selectionOpen">
            <icon-caret-down />
          </button>
        </div>
      </div>
    </div>
    <ul
      class="check-list__result-list"
      v-if="relevantTree && selectionOpen">
      <check-list-child
        v-for="item in relevantTree"
        :key="item.term_id"
        :flat-list="flatTree"
        :child-item="item"
        :selected-items="selectedItems"
        @on-change="handleChange($event)"/>
    </ul>
  </div>
</template>

<script lang="ts">
import IconCaretDown from "assets/icons/IconCaretDown.svg";
import CheckListChild from "~/components/UI/CheckListChild.vue";
import { useSearchStore } from "~/stores/searchStore";
import { defineComponent } from "#imports";

export interface TreeItem {
  name: string;
  slug: string;
  term_id: string;
  parent: string;
  description: string;
  count: string;
}

export default defineComponent({
  setup () {
    return {
      searchStore: useSearchStore(),
    }
  },
  components: {
    CheckListChild,
    IconCaretDown,
  },
  props: {
    listName: String,
    filterKey: String,
  },
  data () {
    return {
      selectionOpen: false,
      parentsOpen: [],
    };
  },
  computed: {
    flatTree () {
      const flatTree = this.searchStore.data[this.filterKey]

      // fetch and merge any missing active tag filter attributes
      if (this.filterKey === 'tags') {
        const activeFilters = this.searchStore.filters[this.filterKey]

        activeFilters.forEach(activeTagSlug => {
          if (! flatTree.find((filter) => filter.slug === activeTagSlug)) {
            // Inject missing tag to top of list
            flatTree.unshift({
              slug: activeTagSlug,
              name: activeTagSlug.charAt(0).toUpperCase() + activeTagSlug.slice(1),
              parent: 0,
            })
          }
        })
      }

      return flatTree
    },
    relevantTree (): TreeItem[] {
      if (!this.flatTree) {
        return [];
      }

      const tree = this.flatTree.filter((item) => !item.parent || item.parent === '0');

      return tree.sort((a, b) => a.name < b.name ? -1 : 1);
    },
    selectedItems () {
      if (this.filterKey === 'categories') {
        return [
          ...this.searchStore.filters.categories,
          ...this.searchStore.urlFilters.categories,
        ];
      }
      return [
        ...this.searchStore.filters.tags,
        ...this.searchStore.urlFilters.tags,
      ];
    },
    rootClasses () {
      return {
        'check-list--open': this.selectionOpen,
      };
    },
  },
  methods: {
    handleChange (item: TreeItem) {
      if (this.searchStore.urlFilters.categories.includes(item.slug)) {
        this.searchStore.toggleFilter('urlCategories', item.slug);
      } else if (this.searchStore.urlFilters.tags.includes(item.slug)) {
        this.searchStore.toggleFilter('urlTags', item.slug);
      } else {
        this.searchStore.toggleFilter(this.filterKey, item.slug);
      }

      this.$emit('filter-change-event')
    },
  },
});
</script>

<style lang="scss">
@use "~/common/scss/helpers";

.check-list {
  $root: &;

  &--open {

    #{$root}__meta-toggle svg {
      transform: rotate(180deg);
    }
  }

  &__meta {
    align-items: center;
    display: flex;
    gap: helpers.px-to-rem(3);
  }

  &__meta-toggle {
    align-items: center;
    display: flex;
    height: helpers.px-to-rem(16);
    width: helpers.px-to-rem(16);
    color: #000;
  }

  &__result-list {
    list-style: none;
    padding: helpers.px-to-rem(20) 0 0;
  }

  &__child-result-list {
    list-style: none;
    padding-left: helpers.px-to-rem(15);
  }

  .shop-page__filter-section-header {
    cursor: pointer;
  }
}
</style>
